import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { GoalCollectionSaveRequestDtoWithFlag } from '../../../store/landscapeAPI/goal/dto/request/goal-collection-save-request.dto';
import { GoalThemeSaveRequestDtoWithFlag } from '../../../store/landscapeAPI/goal/dto/request/goal-theme-save-request.dto';
import { GoalUpdateRequestDto } from '../../../store/landscapeAPI/goal/dto/request/goal-update-request.dto';
import { GoalResponseDto } from '../../../store/landscapeAPI/goal/dto/response/goal-response.dto';
import {
  fetchGoalById,
  updateGoal,
} from '../../../store/landscapeAPI/goal/reducers/goal.slice';
import {
  createGoalCollection,
  deleteGoalCollection,
  fetchGoalCollectionsByGoalId,
} from '../../../store/landscapeAPI/goal/reducers/goalCollection.slice';
import {
  createGoalTheme,
  deleteGoalTheme,
  fetchGoalThemesByGoalId,
} from '../../../store/landscapeAPI/goal/reducers/goalTheme.slice';
import { GoalIndicatorSaveRequestDtoWithFlag } from '../../../store/landscapeAPI/indicator/dto/request/indicator-goal-save-request.dto';
import {
  deleteIndicatorGoal,
  fetchIndicatorGoalsByGoalId,
  saveIndicatorGoal,
} from '../../../store/landscapeAPI/indicator/reducers/indicatorGoal.slice';
import { AppDispatch, RootState } from '../../../store/store';
import Spinner from '../../atoms/Spinner/Spinner';
import { ReportingDetailsGoalsEditTemplate } from '../../templates';
import {
  getLanguageKey,
  LocalesLanguagesConstants,
} from '../../../locales/locales';
import {
  translateObject,
  TranslateParams,
} from '../../../store/translateAPI/translate/reducers/translate.slice';

import styles from './ReportingDetailsGoalsEditPage.module.css';
import cn from 'classnames';
import { showSuccess } from '../../../utils/Notifications';

interface ReportingDetailsGoalsEditPageProps {}

const ReportingDetailsGoalsEditPage: FC<
  ReportingDetailsGoalsEditPageProps
> = () => {
  const [goal, setGoal] = useState<GoalResponseDto>();

  const { id } = useParams();

  const navigate = useNavigate();

  const {
    goal: selector,
    status,
    error,
  } = useSelector((state: RootState) => state.goalSlice);

  const { filteredGoalThemes: goalThemesSelector, status: goalThemesStatus } =
    useSelector((state: RootState) => state.goalThemeSlice);

  const { status: translateStatus } = useSelector(
    (state: RootState) => state.translateSlice
  );

  const [goalThemes, setGoalThemes] = useState<
    GoalThemeSaveRequestDtoWithFlag[]
  >([]);

  const {
    filteredGoalCollections: goalCollectionsSelector,
    status: goalCollectionsStatus,
  } = useSelector((state: RootState) => state.goalCollectionSlice);

  const [goalCollections, setGoalCollections] = useState<
    GoalCollectionSaveRequestDtoWithFlag[]
  >([]);

  const {
    filteredIndicatorGoals: goalIndicatorsSelector,
    status: goalIndicatorsStatus,
  } = useSelector((state: RootState) => state.indicatorGoalSlice);

  const [goalIndicators, setGoalIndicators] = useState<
    GoalIndicatorSaveRequestDtoWithFlag[]
  >([]);

  const dispatch = useDispatch<AppDispatch>();

  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const translate = async (toLanguage: LocalesLanguagesConstants) => {
    if (!goal) return;
    const params: TranslateParams<GoalResponseDto> = {
      obj: goal,
      from: getLanguageKey(fromLanguage)!,
      to: [getLanguageKey(toLanguage)!],
    };

    const translatedResponse = (await dispatch(
      translateObject(params)
    ).unwrap()) as GoalResponseDto;

    const updateRequest: GoalUpdateRequestDto = {
      name: translatedResponse.name,
      type: goal.type,
      id: goal.id,
      published: goal.published,
    };

    await dispatch(
      updateGoal({
        updateRequest,
        language: toLanguage,
      })
    ).unwrap();
    setLanguage(toLanguage);
  };

  const generateAllTranslations = async () => {
    return await Promise.allSettled(
      Object.values(LocalesLanguagesConstants).map(translate)
    );
  };

  const generateTranslation = async () => {
    return await translate(toLanguage);
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchGoalById({ id: id, language: selectedLanguage }));
      dispatch(fetchGoalThemesByGoalId(id));
      dispatch(fetchGoalCollectionsByGoalId(id));
      dispatch(fetchIndicatorGoalsByGoalId(id));
    }
  }, [id, dispatch, selectedLanguage]);

  useEffect(() => {
    if (status === 'succeeded' && selector) {
      setGoal(selector);
    }
  }, [selector, status]);

  const handleOnGoalThemesChange = (
    goalThemes: GoalThemeSaveRequestDtoWithFlag[]
  ) => {
    setGoalThemes(goalThemes);
  };

  const handleOnGoalCollectionsChange = (
    goalCollections: GoalCollectionSaveRequestDtoWithFlag[]
  ) => {
    setGoalCollections(goalCollections);
  };

  const handleOnGoalIndicatorsChange = (
    goalIndicators: GoalIndicatorSaveRequestDtoWithFlag[]
  ) => {
    setGoalIndicators(goalIndicators);
  };

  const handleOnChange = (goal: GoalResponseDto) => {
    setGoal(goal);
  };

  const handleEditGoal = async (goal: GoalResponseDto) => {
    const data: GoalUpdateRequestDto = {
      id: goal.id,
      name: goal.name,
      type: goal.type,
      published: goal.published,
    };

    await dispatch(
      updateGoal({ updateRequest: data, language: selectedLanguage })
    );

    await Promise.all([
      ...goalThemes.map((goalTheme) => {
        if (!goalTheme.toBeDeleted) {
          return dispatch(createGoalTheme(goalTheme));
        } else {
          return dispatch(deleteGoalTheme(goalTheme.id));
        }
      }),

      ...goalCollections.map((goalCollection) => {
        if (!goalCollection.toBeDeleted) {
          return dispatch(createGoalCollection(goalCollection));
        } else {
          return dispatch(deleteGoalCollection(goalCollection.id));
        }
      }),

      ...goalIndicators.map((goalIndicator) => {
        if (!goalIndicator.toBeDeleted) {
          return dispatch(saveIndicatorGoal(goalIndicator));
        } else {
          return dispatch(deleteIndicatorGoal(goalIndicator.id));
        }
      }),
    ]);

    showSuccess('Goal saved successfully.');
    navigate('/reporting-details/goals');
  };

  if (
    status === 'loading' ||
    goalThemesStatus === 'loading' ||
    goalCollectionsStatus === 'loading' ||
    goalIndicatorsStatus === 'loading' ||
    translateStatus === 'loading'
  ) {
    return <Spinner />;
  }

  if (status === 'failed') {
    return <div>{error}</div>;
  }

  return (
    <div className={cn(styles.wrapper)}>
      {goal && (
        <ReportingDetailsGoalsEditTemplate
          data={goal}
          goalThemes={goalThemesSelector ?? []}
          handleOnGoalThemesChange={handleOnGoalThemesChange}
          goalCollections={goalCollectionsSelector ?? []}
          handleOnGoalCollectionsChange={handleOnGoalCollectionsChange}
          goalIndicators={goalIndicatorsSelector ?? []}
          handleOnGoalIndicatorsChange={handleOnGoalIndicatorsChange}
          handleEditGoal={handleEditGoal}
          handleOnChange={handleOnChange}
          setLanguage={setLanguage}
          selectedLanguage={selectedLanguage}
          fromLanguage={fromLanguage}
          setFromLanguage={setFromLanguage}
          toLanguage={toLanguage}
          setToLanguage={setToLanguage}
          generateTranslation={generateTranslation}
          generateAllTranslations={generateAllTranslations}
        />
      )}
    </div>
  );
};

export default ReportingDetailsGoalsEditPage;
